import { useCallback, useEffect, useRef, useState } from "react";

export interface Bill {
  id: number;
  x: number;
  y: number;
  rotation: number;
  isAnimating: boolean;
  finalY: number;
}

// Legacy export for backward compatibility
export type DollarBill = Bill;

interface UseDollarBillAnimationProps {
  targetBillCount: number;
  billWidth: number;
  jarX: number;
  jarY: number;
  jarWidth: number;
  jarHeight: number;
}

export function useDollarBillAnimation(props: UseDollarBillAnimationProps) {
  const { targetBillCount, billWidth, jarX, jarWidth, jarHeight, jarY } = props;
  const [bills, setBills] = useState<Bill[]>([]);
  const animationFrameRef = useRef<number>();
  const billIdCounter = useRef(0);

  const calculateBillPosition = useCallback(
    (index: number) => {
      const overlapAmount = 5;
      const jarCenterX = jarX + jarWidth / 2;
      const jarBottom = jarY + jarHeight;
      const x = jarCenterX - billWidth / 2 + (Math.random() * 10 - 5);
      const y = jarBottom - (index + 1) * overlapAmount - 35;

      return { x, y };
    },
    [billWidth, jarX, jarWidth, jarHeight, jarY]
  );

  const animateBills = useCallback(() => {
    setBills((previousBills) => {
      let needsUpdate = false;
      const updatedBills = previousBills.map((bill) => {
        if (bill.isAnimating) {
          needsUpdate = true;
          const newY = bill.y + 4;
          const rotationTarget = bill.finalY - bill.y < 30 ? bill.rotation * 0.9 : bill.rotation;

          if (newY >= bill.finalY) {
            return {
              ...bill,
              y: bill.finalY,
              rotation: bill.rotation * 0.95,
              isAnimating: false,
            };
          }

          return {
            ...bill,
            y: newY,
            rotation: rotationTarget,
          };
        }

        return bill;
      });

      if (needsUpdate) {
        animationFrameRef.current = requestAnimationFrame(animateBills);
      }

      return updatedBills;
    });
  }, []);

  useEffect(() => {
    const currentBillCount = bills.length;

    if (targetBillCount > currentBillCount) {
      const billsToAdd = targetBillCount - currentBillCount;
      const newBills: Bill[] = [];

      for (let billIndex = 0; billIndex < billsToAdd; billIndex += 1) {
        const totalBillIndex = currentBillCount + billIndex;
        const { x, y } = calculateBillPosition(totalBillIndex);

        billIdCounter.current += 1;
        newBills.push({
          id: billIdCounter.current,
          x,
          y: -50,
          rotation: Math.random() * 30 - 15,
          isAnimating: true,
          finalY: y,
        });
      }

      setBills((previous) => [...previous, ...newBills]);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(animateBills);
    } else if (targetBillCount < currentBillCount) {
      setBills((previous) => previous.slice(0, targetBillCount));
    }
  }, [targetBillCount, bills.length, calculateBillPosition, animateBills]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return { bills };
}
